<script lang="ts" setup>
  import { ref, onMounted, onBeforeUnmount } from 'vue'

  import { useFilesStore } from '@clevver/stores/files.store'
  import UploadView from '@clevver/components/upload-view.vue'

  const filesStore = useFilesStore()

  const modalRef = ref<HTMLElement | null>(null)
  const uploadView = ref<InstanceType<typeof UploadView> | null>(null)
  const dismissRef = ref<HTMLButtonElement | null>(null)

  // Bootstrap fires this once the modal is fully hidden — clear the queue so the
  // next open starts fresh.
  const onHidden = () => uploadView.value?.reset()

  // Upload finished: refresh the grid + quota so the library reflects the new
  // files behind the still-open success state. The user dismisses via the footer.
  const onComplete = () => {
    filesStore.getFiles()
    filesStore.fetchQuota()
  }

  const requestClose = () => dismissRef.value?.click()

  onMounted(() => modalRef.value?.addEventListener('hidden.bs.modal', onHidden))
  onBeforeUnmount(() => modalRef.value?.removeEventListener('hidden.bs.modal', onHidden))
</script>

<template>
  <!--
    Bootstrap owns open/close (triggered via data-bs-toggle in the topbar + tab bar).
    The native backdrop is disabled so this element can draw its own blur scrim;
    outside-click-to-close is reconstructed via @mousedown.self, which works because
    Bootstrap sets pointer-events:none on .modal-dialog (clicks fall through to here).
  -->
  <div
    id="uploadModal"
    ref="modalRef"
    class="modal fade upload-modal"
    tabindex="-1"
    aria-modal="true"
    aria-label="Upload files"
    data-bs-backdrop="false"
    @mousedown.self="requestClose"
  >
    <div class="modal-dialog modal-dialog-centered upload-modal__dialog">
      <div class="modal-content upload-modal__panel">
        <UploadView ref="uploadView" @complete="onComplete" @close="requestClose" />
      </div>
    </div>

    <!-- Programmatic dismiss target so child events can close via Bootstrap. -->
    <button ref="dismissRef" type="button" class="upload-modal__dismiss" data-bs-dismiss="modal" aria-hidden="true" tabindex="-1"></button>
  </div>
</template>

<style scoped lang="scss">
  // Dim + blur overlay (replaces Bootstrap's plain backdrop, which is disabled
  // via data-bs-backdrop="false" so this element carries the scrim itself). The
  // scrim/shadow ink is a fixed dark by design — it must not flip with the theme.
  .upload-modal {
    --bs-modal-width: 524px;
    background: color-mix(in srgb, #14141c 52%, transparent);
    -webkit-backdrop-filter: blur(3px);
    backdrop-filter: blur(3px);
  }

  // Rise-in (overrides Bootstrap's default vertical slide).
  .upload-modal.fade .modal-dialog {
    transform: translateY(10px) scale(0.99);
    transition: transform 0.22s cubic-bezier(0.16, 0.84, 0.34, 1), opacity 0.22s;
  }
  .upload-modal.show .modal-dialog { transform: none; }

  @media (prefers-reduced-motion: reduce) {
    .upload-modal.fade .modal-dialog { transition: none; }
  }

  .upload-modal__panel {
    max-height: min(88vh, 780px);
    background: var(--c-bg);
    border: 1px solid var(--c-line);
    border-radius: 16px;
    box-shadow:
      0 30px 80px rgba(15, 16, 24, 0.34),
      0 8px 24px rgba(15, 16, 24, 0.18);
    overflow: hidden;
  }

  // Offscreen, non-interactive — only ever clicked programmatically.
  .upload-modal__dismiss {
    position: fixed;
    top: -1000px;
    left: -1000px;
    width: 1px;
    height: 1px;
    opacity: 0;
    border: 0;
    background: transparent;
    pointer-events: none;
  }

  // Full-bleed on small screens, mirroring the mock.
  @media (max-width: 560px) {
    .upload-modal .modal-dialog {
      margin: 0;
      max-width: 100%;
      min-height: 100%;
    }
    .upload-modal__panel {
      max-height: 100dvh;
      border: 0;
      border-radius: 0;
    }
  }
</style>
